import { ACCOUNT_TOTALS_BY_ACCOUNT_PATH } from 'core/cuenta/routes';
import { SearchCardMovementsRequest } from 'core/models/SearchCardMovementsRequest';
import { useCoreService } from 'core/services/useCoreService';
import { createContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { CARDS_TOTALS_PATH, SEARCH_CARD_AUTHORIZATIONS_PATH } from '../routes';
import { useDataFetcher } from 'shared/http';
import { LoadingSpinner } from 'shared/spinner/components/LoadingSpinner';
import { Movimiento } from '../models/Movimiento';
import { CardReport } from '../models/CardReport';
import { Pageable } from 'shared/table/models/Pageable';

interface ICardAuthorizationsContext {
  goBack(): void;
  goToAccountMovements(): void;
  goToCardMovements(): void;
  cardReport: CardReport;
  authorizations: Pageable<Movimiento>;
  authSearch: boolean;
}

export const CardAuthorizationsContext = createContext<ICardAuthorizationsContext>({
  goBack: () => {},
  goToAccountMovements: () => {},
  goToCardMovements: () => {},
  cardReport: null,
  authorizations: null,
  authSearch: false,
});

export const CardAuthorizationsProvider = ({ children }) => {
  const { state, pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const [cardReport, setCardReportRequest] = useDataFetcher<CardReport>(null);
  const [authSearch] = useState<boolean>(state && state['fromAuthSearch']);
  const [cardNumber] = useState<string>(state && state['tarjeta']);
  const navigate = useNavigate();
  const coreService = useCoreService();

  useEffect(() => {
    const request = new SearchCardMovementsRequest({
      numeroTarjeta: state['tarjeta'],
    });
    setCardReportRequest(() =>
      coreService.getCardAuthorizations(request).then((resp) => {
        return resp.body;
      }),
    );
  }, [cardNumber, coreService, state, navigate, searchParams, setCardReportRequest]);

  const contextValue = useMemo(() => {
    return {
      cardReport: cardReport.data,
      authorizations: cardReport.data?.movimientos,
      authSearch,
      goBack: () => {
        let path = state['fromPath'];
        if (state && state['fromAuthSearch']) {
          path = SEARCH_CARD_AUTHORIZATIONS_PATH;
        }
        navigate(
          {
            pathname: path,
            search: searchParams.toString(),
          },
          {
            state: {
              tarjeta: cardNumber,
            },
          },
        );
      },
      goToAccountMovements: () => {
        navigate(
          {
            pathname: ACCOUNT_TOTALS_BY_ACCOUNT_PATH.replace(
              ':accountNumber',
              cardReport.data.cuentaOrigen,
            ),
            search: searchParams.toString(),
          },
          {
            state: {
              tarjeta: cardNumber,
              fromPath: pathname,
              fromAuthSearch: authSearch,
            },
          },
        );
      },
      goToCardMovements: () => {
        navigate(
          {
            pathname: CARDS_TOTALS_PATH.replace(
              ':cardNumber',
              cardNumber.replace(/\d(?=\d{4})/g, 'X'),
            ),
            search: searchParams.toString(),
          },
          {
            state: {
              tarjeta: cardNumber,
              fromPath: pathname,
              fromAuthSearch: authSearch,
            },
          },
        );
      },
    };
  }, [authSearch, cardNumber, cardReport.data, navigate, pathname, searchParams, state]);

  return (
    <CardAuthorizationsContext.Provider value={contextValue}>
      <LoadingSpinner isLoading={cardReport.isLoading}>
        {cardReport.data && children}
      </LoadingSpinner>
    </CardAuthorizationsContext.Provider>
  );
};
